import React, { CSSProperties, ReactNode } from 'react';
import { useFocusRing } from 'react-aria';
import {
  Dialog,
  DialogProps,
  Heading,
  Modal,
  ModalOverlay,
  ModalOverlayProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import useFullscreenContainer from '../shared/useFullscreenContainer';
import { cx } from '../util';
import { ClarionButton } from './ClarionButton';
import Divider from './Divider';
import { useDraggable } from './useDraggable';
import { focusRing } from './utils';

const handleStyles = tv({
  extend: focusRing,
  base: 'absolute -inset-x-5 cursor-grab active:cursor-grabbing rounded-t-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors duration-150 -outline-offset-2',
  variants: {
    hasHeader: {
      true: '-top-4 -bottom-2',
      false: '-top-4 h-12',
    },
  },
});

type ClarionDialogProps = DialogProps & {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: ReactNode;
  isDismissable?: boolean;
  showCloseButton?: boolean;
  maxWidth?: string | number;
};

export function ClarionDialog({
  className,
  children,
  title,
  isOpen,
  onClose,
  isDismissable = true,
  style,
  showCloseButton = false,
  maxWidth,
  ...props
}: ClarionDialogProps) {
  const fullscreenElement = useFullscreenContainer();
  const { isFocusVisible, focusProps } = useFocusRing();
  const { handleRef, targetRef, ...draggableEvents } = useDraggable<HTMLButtonElement>({
    isEnabled: isOpen,
  });

  return (
    <ClarionModal
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => !newIsOpen && onClose?.()}
      isDismissable={isDismissable}
      style={style}
      maxWidth={maxWidth}
      UNSTABLE_portalContainer={fullscreenElement}
      modalRef={targetRef}
    >
      <Dialog
        {...props}
        className={cx('outline-0 p-5 pt-4 max-h-[inherit] relative bg-inherit', className)}
      >
        <div className={cx('relative', title || showCloseButton ? 'mb-4 min-h-8' : 'h-0')}>
          {/* eslint-disable-next-line local-rules/no-raw-button -- RAC Button doesn’t properly handle the captured pointer events */}
          <button
            ref={handleRef}
            aria-label="Drag or use arrow keys to move"
            className={handleStyles({ isFocusVisible, hasHeader: !!(title || showCloseButton) })}
            {...draggableEvents}
            {...focusProps}
          />
          <div className="relative pointer-events-none flex justify-between items-start w-full">
            {title && (
              <Heading slot="title" className="text-2xl font-bold">
                {title}
              </Heading>
            )}
            {showCloseButton && (
              <ClarionButton
                icon="cross"
                variant="minimal"
                size="large"
                onClick={onClose}
                className="-mr-2 -mt-1 pointer-events-auto"
                tooltip="Close"
              />
            )}
          </div>
        </div>
        {children}
      </Dialog>
    </ClarionModal>
  );
}

type ClarionDialogFooterProps = {
  className?: string;
  children: ReactNode;
};

// Sticky footer that right-aligns action buttons
export function ClarionDialogFooter({ className, children }: ClarionDialogFooterProps) {
  return (
    <div className={`sticky bottom-0 my-0 bg-gray-50 dark:bg-neutral-800 mt-5`}>
      <Divider className="mb-5" />
      <div className={cx('flex justify-end gap-2 items-center pb-5 -mb-5', className)}>
        {children}
      </div>
    </div>
  );
}

// Internal modal backdrop and container
function ClarionModal({
  style,
  maxWidth,
  children,
  modalRef,
  ...props
}: ModalOverlayProps & {
  maxWidth?: string | number;
  modalRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const modalStyle: CSSProperties = {
    ...(style || {}),
    position: 'relative',
    ...(maxWidth
      ? {
          width: '90vw',
          maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        }
      : {}),
  };

  return (
    <ModalOverlay
      {...props}
      // Use the visual viewport height on touch devices to account for the iOS virtual keyboard.
      // This solution is not perfect, but is better than being unable to scroll to input fields at the bottom of a dialog.
      className="fixed inset-inline-0 w-full top-0 h-full pointer-coarse:h-(--visual-viewport-height) z-20 bg-black/35 grid place-items-center motion-safe:animate-fade-in ease-out"
      // Sticky headers in dialogs shouldn't be affected by the header height outside of the dialog
      style={{ '--header-height': '0px' } as CSSProperties}
    >
      <Modal
        ref={modalRef}
        style={modalStyle}
        className={cx(
          'max-h-full bg-gray-50 dark:bg-neutral-800 shadow-2xl bg-clip-padding border border-black/10 dark:border-white/30 motion-safe:animate-slide-in overflow-auto relative forced-colors:border-2',
          'max-sm:!w-dvw max-sm:!max-w-dvw max-sm:!h-dvh max-sm:!max-h-dvh max-sm:rounded-none sm:rounded-xl',
          !maxWidth && 'max-w-dvw sm:max-w-2xl',
        )}
      >
        {children}
      </Modal>
    </ModalOverlay>
  );
}
